import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore.js';
import { useUiStore } from '../../store/uiStore.js';
import { useNotificationStore } from '../../store/notificationStore.js';
import { dashboardApi } from '../../api/dashboard.api.js';
import { workspaceNav } from '../../utils/navigation.js';
import { hasAutomation, hasCalls, hasCampaigns, hasInbox } from '../../utils/tenantFeatures.js';
import { AttentionList, SEVERITY_COLOR } from '../shared/AttentionList.jsx';
import { timeAgo } from '../../utils/format.js';
import {
  IconBell, IconChat, IconChevronDown, IconContacts, IconLightning, IconMegaphone, IconPhone, IconPlus, IconSearch, IconX,
} from '../icons/Icons.jsx';

function useOutsideClose(ref, open, onClose) {
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (!ref.current?.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, ref]);
}

const iconButton = 'relative w-10 h-10 grid place-items-center rounded-[10px] transition-colors hover:bg-[var(--neutralSoft)]';

function NewMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const tenant = useAuthStore((s) => s.tenant);
  const role = useAuthStore((s) => s.user?.role);
  useOutsideClose(ref, open, () => setOpen(false));
  const staff = ['ADMIN', 'MANAGER'].includes(role);

  const items = [
    hasInbox(tenant) && { label: 'Message', hint: 'Start a conversation', Icon: IconChat, to: '/inbox?compose=1' },
    staff && { label: 'Contact', hint: 'Add one person', Icon: IconContacts, to: '/contacts?new=1' },
    staff && hasCampaigns(tenant) && { label: 'Campaign', hint: 'Send to a tag', Icon: IconMegaphone, to: '/campaigns/new' },
    staff && hasAutomation(tenant) && { label: 'Automation', hint: 'Start from a template', Icon: IconLightning, to: '/automations?new=1' },
  ].filter(Boolean);
  if (!items.length) return null;

  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen((v) => !v)} className="btn-primary !min-h-[38px] !px-3 sm:!px-3.5" aria-haspopup="menu" aria-expanded={open}>
        <IconPlus size={16} />
        <span className="hidden sm:inline">New</span>
        <IconChevronDown size={14} className="hidden sm:block" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 z-50 w-60 p-1.5 rounded-[12px]" style={{ background: 'var(--card)', border: '1px solid var(--border)', boxShadow: 'var(--shadowFloat)' }} role="menu">
          {items.map(({ label, hint, Icon, to }) => (
            <button
              key={label}
              type="button"
              role="menuitem"
              onClick={() => { setOpen(false); navigate(to); }}
              className="w-full flex items-center gap-3 rounded-[9px] px-2.5 py-2 text-left hover:bg-[var(--bg)]"
            >
              <span className="w-8 h-8 rounded-[9px] grid place-items-center flex-shrink-0" style={{ background: 'var(--accentSoft)', color: 'var(--accent)' }}><Icon size={16} /></span>
              <span>
                <span className="block text-sm font-semibold" style={{ color: 'var(--text)' }}>{label}</span>
                <span className="block text-xs" style={{ color: 'var(--textMuted)' }}>{hint}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { attention, events, fetch, markAllSeen, seen } = useNotificationStore();
  const location = useLocation();
  useOutsideClose(ref, open, () => setOpen(false));

  useEffect(() => {
    fetch();
    const timer = setInterval(fetch, 60000);
    const onFocus = () => fetch();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(timer); window.removeEventListener('focus', onFocus); };
  }, [fetch]);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const unseen = [...attention, ...events].filter((item) => !seen.has(item.id)).length;
  const toggle = () => {
    setOpen((v) => !v);
    if (!open) markAllSeen();
  };

  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={toggle} className={iconButton} style={{ color: 'var(--textMuted)' }} aria-label={`Notifications${unseen ? `, ${unseen} new` : ''}`} aria-expanded={open}>
        <IconBell size={19} />
        {unseen > 0 && (
          <span className="absolute top-1.5 right-1.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold grid place-items-center" style={{ background: 'var(--danger)', color: '#fff' }}>
            {unseen > 9 ? '9+' : unseen}
          </span>
        )}
      </button>
      {open && (
        <div className="fixed sm:absolute inset-x-3 sm:inset-x-auto sm:right-0 top-[68px] sm:top-full sm:mt-2 z-50 sm:w-[400px] max-h-[min(70vh,560px)] overflow-y-auto rounded-[14px]" style={{ background: 'var(--card)', border: '1px solid var(--border)', boxShadow: 'var(--shadowFloat)' }}>
          <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid var(--border)' }}>
            <p className="text-sm font-bold" style={{ color: 'var(--text)' }}>Notifications</p>
            <button type="button" onClick={() => setOpen(false)} className="w-7 h-7 grid place-items-center rounded-lg hover:bg-[var(--neutralSoft)]" style={{ color: 'var(--textMuted)' }} aria-label="Close notifications"><IconX size={15} /></button>
          </div>
          <div className="p-3 space-y-4">
            <div>
              <p className="side-nav-label !px-1">Needs attention</p>
              <AttentionList items={attention} onNavigate={() => setOpen(false)} />
            </div>
            {events.length > 0 && (
              <div>
                <p className="side-nav-label !px-1">Recent</p>
                <ul className="grid gap-1">
                  {events.map((event) => (
                    <li key={event.id}>
                      <Link to={event.action?.to || '/dashboard'} onClick={() => setOpen(false)} className="flex items-start gap-3 rounded-[10px] px-2.5 py-2 hover:bg-[var(--bg)]">
                        <span className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0" style={{ background: SEVERITY_COLOR[event.severity] || 'var(--accent)' }} />
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-semibold" style={{ color: 'var(--text)' }}>{event.title}</span>
                          <span className="block text-xs" style={{ color: 'var(--textMuted)' }}>{event.detail}</span>
                        </span>
                        <span className="text-[11px] flex-shrink-0" style={{ color: 'var(--textDim)' }}>{timeAgo(event.at)}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CommandPalette() {
  const open = useUiStore((s) => s.searchOpen);
  const setOpen = useUiStore((s) => s.setSearchOpen);
  const tenant = useAuthStore((s) => s.tenant);
  const role = useAuthStore((s) => s.user?.role);
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setOpen]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setResults(null);
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [open]);

  useEffect(() => {
    if (!open || query.trim().length < 2) { setResults(null); return undefined; }
    const timer = setTimeout(async () => {
      try {
        const { data } = await dashboardApi.search(query.trim());
        setResults(data);
      } catch {
        setResults({ contacts: [], campaigns: [], automations: [], links: [] });
      }
    }, 220);
    return () => clearTimeout(timer);
  }, [query, open]);

  const pages = useMemo(() => {
    const nav = workspaceNav(tenant, role).flatMap((s) => s.items.map((i) => ({ label: i.label, to: i.path, Icon: i.Icon })));
    const extras = [];
    if (role === 'ADMIN') {
      extras.push(
        { label: 'Team members', to: '/settings?section=team' },
        { label: 'Billing and plan', to: '/settings?section=billing' },
        { label: 'API keys and developers', to: '/settings?section=developers' },
        { label: 'Activity log', to: '/settings?section=activity' },
        { label: 'Saved replies', to: '/settings?section=replies' },
      );
    }
    if (['ADMIN', 'MANAGER'].includes(role) && hasCampaigns(tenant)) extras.push({ label: 'New campaign', to: '/campaigns/new' });
    const q = query.trim().toLowerCase();
    return [...nav, ...extras].filter((p) => !q || p.label.toLowerCase().includes(q)).slice(0, q ? 6 : 8);
  }, [tenant, role, query]);

  const flat = useMemo(() => {
    const rows = pages.map((p) => ({ group: 'Pages', label: p.label, to: p.to }));
    if (results) {
      results.contacts?.forEach((c) => rows.push({ group: 'Contacts', label: c.name || c.phone || c.email, hint: c.name ? (c.phone || c.email) : c.email, to: `/contacts/${c.id}` }));
      results.campaigns?.forEach((c) => rows.push({ group: 'Campaigns', label: c.name, hint: `${c.channel} · ${c.status.toLowerCase()}`, to: `/campaigns/${c.id}` }));
      results.automations?.forEach((a) => rows.push({ group: 'Automations', label: a.name, hint: a.status.toLowerCase(), to: `/automations/${a.id}` }));
      results.links?.forEach((l) => rows.push({ group: 'Short links', label: l.label || l.code, hint: l.originalUrl, to: '/links' }));
    }
    return rows;
  }, [pages, results]);

  useEffect(() => { setActive(0); }, [flat.length]);

  if (!open) return null;
  const go = (row) => { setOpen(false); navigate(row.to); };
  const onKeyDown = (e) => {
    if (e.key === 'Escape') setOpen(false);
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(flat.length - 1, i + 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); }
    if (e.key === 'Enter' && flat[active]) go(flat[active]);
  };

  let lastGroup = '';
  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center px-3 pt-[10vh]" style={{ background: 'var(--overlay)' }} onMouseDown={() => setOpen(false)}>
      <div className="w-full max-w-[600px] rounded-[14px] overflow-hidden" style={{ background: 'var(--card)', border: '1px solid var(--border)', boxShadow: 'var(--shadowFloat)' }} onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-4" style={{ borderBottom: '1px solid var(--border)' }}>
          <IconSearch size={18} style={{ color: 'var(--textDim)' }} />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search contacts, campaigns, automations or pages…"
            className="flex-1 h-14 bg-transparent outline-none text-[15px]"
            style={{ color: 'var(--text)' }}
            aria-label="Search"
          />
          <kbd className="hidden sm:inline text-[11px] px-1.5 py-0.5 rounded" style={{ border: '1px solid var(--border)', color: 'var(--textDim)' }}>Esc</kbd>
        </div>
        <div className="max-h-[60vh] overflow-y-auto p-2">
          {flat.length === 0 && (
            <p className="px-3 py-8 text-center text-sm" style={{ color: 'var(--textMuted)' }}>
              {query.trim().length < 2 ? 'Type at least 2 letters.' : `Nothing found for “${query}”.`}
            </p>
          )}
          {flat.map((row, index) => {
            const header = row.group !== lastGroup ? row.group : null;
            lastGroup = row.group;
            return (
              <div key={`${row.group}-${row.to}-${index}`}>
                {header && <p className="side-nav-label !px-2.5 !mt-2 !mb-1">{header}</p>}
                <button
                  type="button"
                  onMouseEnter={() => setActive(index)}
                  onClick={() => go(row)}
                  className="w-full flex items-center justify-between gap-3 rounded-[9px] px-2.5 py-2 text-left"
                  style={{ background: index === active ? 'var(--accentSoft)' : 'transparent' }}
                >
                  <span className="text-sm font-semibold truncate" style={{ color: index === active ? 'var(--accentHot)' : 'var(--text)' }}>{row.label}</span>
                  {row.hint && <span className="text-xs truncate max-w-[45%]" style={{ color: 'var(--textMuted)' }}>{row.hint}</span>}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function TopBar() {
  const tenant = useAuthStore((s) => s.tenant);
  const user = useAuthStore((s) => s.user);
  const setSearchOpen = useUiStore((s) => s.setSearchOpen);
  const setAskAiOpen = useUiStore((s) => s.setAskAiOpen);
  const togglePhone = useUiStore((s) => s.togglePhone);
  const phone = useUiStore((s) => s.phone);
  const role = user?.role;
  const showAskAi = hasAutomation(tenant) && tenant?.askAiBubbleEnabled !== false && ['ADMIN', 'MANAGER'].includes(role);
  const showPhone = hasCalls(tenant);
  const brandName = tenant?.brandName || tenant?.name || 'Workspace';
  const phoneDot = phone.live ? 'var(--success)' : phone.hasError ? 'var(--warning)' : phone.status === 'Connected' ? 'var(--success)' : 'var(--textDim)';

  return (
    <>
      <header className="relative z-30 h-16 flex-shrink-0 flex items-center gap-2 px-3 sm:px-6" style={{ background: 'color-mix(in srgb, var(--surface) 88%, transparent)', borderBottom: '1px solid var(--border)', backdropFilter: 'blur(8px)' }}>
        <Link to="/dashboard" className="md:hidden flex items-center gap-2 min-w-0 mr-1">
          <span
            className="noir-brand-mark w-8 h-8 rounded-[9px] grid place-items-center text-sm font-bold flex-shrink-0"
            style={tenant?.logoUrl ? { backgroundImage: `url(${tenant.logoUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
          >
            {!tenant?.logoUrl && brandName[0]?.toUpperCase()}
          </span>
          <span className="hidden min-[420px]:block text-sm font-semibold truncate max-w-[120px]" style={{ color: 'var(--text)' }}>{brandName}</span>
        </Link>

        <button
          type="button"
          onClick={() => setSearchOpen(true)}
          className="hidden md:flex items-center gap-2.5 h-10 w-full max-w-[420px] rounded-[10px] px-3 text-sm transition-colors hover:border-[var(--borderStrong)]"
          style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--textDim)' }}
        >
          <IconSearch size={16} />
          <span className="flex-1 text-left">Search contacts, campaigns, pages…</span>
          <kbd className="text-[11px] px-1.5 py-0.5 rounded" style={{ border: '1px solid var(--border)', background: 'var(--card)' }}>⌘K</kbd>
        </button>

        <div className="ml-auto flex items-center gap-1 sm:gap-1.5">
          <button type="button" onClick={() => setSearchOpen(true)} className={`${iconButton} md:hidden`} style={{ color: 'var(--textMuted)' }} aria-label="Search">
            <IconSearch size={19} />
          </button>
          {showPhone && (
            <button type="button" onClick={togglePhone} className={`${iconButton} ${phone.live ? '!w-auto px-3 gap-2' : ''}`} style={{ color: phone.live ? 'var(--success)' : 'var(--textMuted)', background: phone.live ? 'var(--successSoft)' : undefined }} aria-label="Phone" title={phone.status || 'Phone'}>
              <IconPhone size={19} />
              {phone.live && <span className="text-xs font-bold tabular-nums">{phone.label}</span>}
              {!phone.live && <span className="absolute top-2 right-2 w-2 h-2 rounded-full" style={{ background: phoneDot, boxShadow: '0 0 0 2px var(--surface)' }} />}
            </button>
          )}
          {showAskAi && (
            <button type="button" onClick={() => setAskAiOpen(true)} className="hidden sm:inline-flex btn-secondary !min-h-[38px] !px-3" aria-label="Ask AI">
              <IconLightning size={16} style={{ color: 'var(--accent)' }} />
              Ask AI
            </button>
          )}
          {showAskAi && (
            <button type="button" onClick={() => setAskAiOpen(true)} className={`${iconButton} sm:hidden`} style={{ color: 'var(--accent)' }} aria-label="Ask AI">
              <IconLightning size={19} />
            </button>
          )}
          <NotificationsBell />
          <NewMenu />
        </div>
      </header>
      <CommandPalette />
    </>
  );
}
